<template>
  <div class="employee-select">
    <v-autocomplete
      :key="`employee-select-${componentKey}`"
      ref="employeeSelectRef"
      :model-value="modelValue.id"
      :custom-filter="customFilter"
      :match-any="true"
      label="Priradený"
      :items="enrichedEmployees"
      :item-title="getItemTitle"
      item-value="id"
      variant="solo"
      class="employee-select__select-field"
      :class="{ error: v$.id.$error }"
      :error-messages="v$.id.$errors.map((e: any) => String(e.$message))"
      @update:model-value="handleUpdateEmployee"
    />

    <div class="employee-select__duration-container">
      <v-text-field
        :model-value="modelValue.duration"
        class="employee-select__duration-field"
        variant="solo"
        type="number"
        label="Časový odhad"
        :class="{ error: v$.duration.$error }"
        :error-messages="v$.duration.$errors.map((e: any) => String(e.$message))"
        append-inner-icon="mdi-clock-outline"
        @update:model-value="handleUpdateDuration"
      />

      <button
        class="employee-select__delete-button"
        :disabled="!modelValue.id"
        @click="handleClearEmployee"
      >
        <v-icon icon="mdi-close" />
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, toRefs, watch, nextTick, ref, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useVuelidate } from '@vuelidate/core';
import { helpers, required } from '@vuelidate/validators';
import type { EmployeeData, CustomFilterFunction } from './types';

interface Props {
  modelValue: EmployeeData;
  customFilter?: CustomFilterFunction;
  required?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  customFilter: undefined,
});

const emit = defineEmits<{
  (e: 'update:modelValue', value: EmployeeData): void;
  (e: 'clearEmployee'): void;
}>();

const store = useStore();

const employeeSelectRef = ref<any>(null);

// Force re-render key
const componentKey = ref(0);

// Ensure all employees have a displayable name
const enrichedEmployees = computed(() => {
  const employees = store.getters['tm/enrichedEmployees'] || [];
  return employees.map((emp: any) => ({
    ...emp,
    _fullName:
      emp._fullName ||
      `${emp.name || ''} ${emp.surname || ''}`.trim() ||
      `Employee ${emp.id}`,
  }));
});

// Function to get item title - handles both objects and IDs
const getItemTitle = (item: any): string => {
  // If item is an object with _fullName, return it
  if (item && typeof item === 'object' && item._fullName) {
    return item._fullName;
  }

  // If item is just an ID (string or number), find the employee
  if (item && (typeof item === 'string' || typeof item === 'number')) {
    const employee = enrichedEmployees.value.find((emp) => emp.id == item);
    return employee?._fullName || `Employee ${item}`;
  }

  return '';
};

// Watch for when employees list changes to force re-render
watch(
  () => enrichedEmployees.value.length,
  (newLength, oldLength) => {
    // Force component re-render when employees are loaded
    if (newLength > 0 && newLength !== oldLength) {
      nextTick(() => {
        componentKey.value++;
      });
    }
  },
  { immediate: true },
);

// Watch for when the selected employee ID changes to ensure display updates
watch(
  () => props.modelValue.id,
  (newId) => {
    // When ID is set and we have employees, force re-render to ensure proper display
    if (newId && enrichedEmployees.value.length > 0) {
      nextTick(() => {
        componentKey.value++;
      });
    }
  },
);

// Make modelValue reactive for Vuelidate
const { modelValue } = toRefs(props);

// Custom validator: if either field is filled, both are required
const requiredWhenPaired = (fieldName: 'id' | 'duration') => {
  return (_value: any) => {
    const { id, duration } = modelValue.value;
    const hasAnyData = !!(id || duration);

    // If no data at all, this is valid (both fields optional)
    if (!hasAnyData) {
      return true;
    }

    // If there's any data, this field must be filled
    if (fieldName === 'id') {
      return !!id;
    } else {
      return duration !== null && duration !== undefined;
    }
  };
};

// Vuelidate rules - only validate if employee has partial data
const rules = computed(() => {
  return props.required
    ? {
        id: { required },
        duration: { required },
      }
    : {
        id: {
          requiredWhenPaired: helpers.withMessage(
            'Vyberte zamestnanca',
            requiredWhenPaired('id'),
          ),
        },
        duration: {
          requiredWhenPaired: helpers.withMessage(
            'Zadajte časový odhad',
            requiredWhenPaired('duration'),
          ),
        },
      };
});

const v$ = useVuelidate(rules.value, modelValue, { $autoDirty: false });

const handleUpdateEmployee = (value: number) => {
  emit('update:modelValue', {
    ...props.modelValue,
    id: value,
  });
};

const handleUpdateDuration = (value: string) => {
  const parsedValue = parseFloat(value);
  emit('update:modelValue', {
    ...props.modelValue,
    duration: isNaN(parsedValue) ? null : parsedValue,
  });
};

const handleClearEmployee = () => {
  emit('clearEmployee');
};

// Expose validate method
const validate = async (required = false) => {
  // If required, check that both fields are set
  if (required) {
    const { id, duration } = modelValue.value;
    if (!id || duration === null || duration === undefined) {
      // Explicitly mark fields as touched to show validation errors
      v$.value.$touch();
      return false;
    }
  }

  // Explicitly mark fields as touched to show validation errors
  v$.value.$touch();
  const isValid = await v$.value.$validate();
  return isValid;
};

onMounted(() => {
  setTimeout(() => {
    console.log('Employee select ref:', employeeSelectRef.value);
  }, 1000);
});

defineExpose({
  validate,
});
</script>

<style lang="sass" scoped>
.employee-select
  display: flex
  justify-content: space-between
  align-items: start
  gap: 10px

  @media (max-width: 768px)
    flex-direction: column
    gap: 5px

  &__duration-container
    display: flex
    justify-content: space-between
    align-items: start
    gap: 10px

  &__select-field
    width: 100%

  &__duration-field
    width: 230px

  &__delete-button
    background: #ff5252
    color: white
    border: none
    border-radius: 4px
    width: 56px
    height: 56px
    font-size: 16px
    cursor: pointer
    display: flex
    align-items: center
    justify-content: center
    transition: all 0.2s
    line-height: 1
    flex-shrink: 0

    &:disabled
      opacity: 0.5
      cursor: not-allowed

    &:hover
      background: #ff1744
</style>
